import { apiFetch, useMockApi } from '@/lib/api/client'

export interface AuditLogDetails {
  category?: string
  reason?: string
  archivedBy?: string
}

export interface AuditLogEntry {
  id: string
  action: string
  entityType?: string
  entityId?: string
  details?: AuditLogDetails
  createdAt: string
}

export interface AuditLogQuery {
  action?: string
  entityType?: string
  entityId?: string
  fromDate?: string
  toDate?: string
  page?: number
  perPage?: number
}

export interface AuditLogApi {
  listAuditLog(query?: AuditLogQuery): Promise<{ items: AuditLogEntry[]; total: number }>
}

function mapAuditLogEntry(body: Record<string, unknown>): AuditLogEntry {
  const details = body.details as Record<string, unknown> | null | undefined

  return {
    id: String(body.id ?? ''),
    action: String(body.action ?? ''),
    entityType: body.entity_type ? String(body.entity_type) : undefined,
    entityId: body.entity_id ? String(body.entity_id) : undefined,
    details: details
      ? {
          category: details.category ? String(details.category) : undefined,
          reason: details.reason ? String(details.reason) : undefined,
          archivedBy: details.archived_by ? String(details.archived_by) : undefined,
        }
      : undefined,
    createdAt: String(body.created_at ?? ''),
  }
}

const MOCK_AUDIT_LOG: AuditLogEntry[] = [
  {
    id: 'audit-1',
    action: 'session_opened',
    entityType: 'chat_session',
    entityId: 'session-gpt4o',
    details: { reason: 'GPT-4o / Coding' },
    createdAt: new Date(Date.now() - 3600_000).toISOString(),
  },
  {
    id: 'audit-2',
    action: 'session_closed',
    entityType: 'chat_session',
    entityId: 'session-claude',
    details: { reason: 'Claude 3.5 / Coding' },
    createdAt: new Date(Date.now() - 1800_000).toISOString(),
  },
  {
    id: 'audit-3',
    action: 'model_archived',
    entityType: 'model_category',
    entityId: 'model-llama',
    details: { category: 'Research', reason: 'archived_due_to_limit', archivedBy: 'system' },
    createdAt: new Date(Date.now() - 900_000).toISOString(),
  },
]

class MockAuditLogApi implements AuditLogApi {
  async listAuditLog(query: AuditLogQuery = {}): Promise<{ items: AuditLogEntry[]; total: number }> {
    let items = [...MOCK_AUDIT_LOG]

    if (query.action) {
      items = items.filter((entry) => entry.action === query.action)
    }

    if (query.entityType) {
      items = items.filter((entry) => entry.entityType === query.entityType)
    }

    const page = query.page ?? 1
    const perPage = query.perPage ?? 50
    const start = (page - 1) * perPage

    return {
      items: items.slice(start, start + perPage),
      total: items.length,
    }
  }
}

class HttpAuditLogApi implements AuditLogApi {
  async listAuditLog(query: AuditLogQuery = {}): Promise<{ items: AuditLogEntry[]; total: number }> {
    const params = new URLSearchParams()
    if (query.action) params.set('action', query.action)
    if (query.entityType) params.set('entity_type', query.entityType)
    if (query.entityId) params.set('entity_id', query.entityId)
    if (query.fromDate) params.set('from_date', query.fromDate)
    if (query.toDate) params.set('to_date', query.toDate)
    if (query.page) params.set('page', String(query.page))
    if (query.perPage) params.set('per_page', String(query.perPage))

    const body = await apiFetch<{ items: unknown[]; total: number }>(`/api/v1/audit-log?${params}`)
    return {
      items: body.items.map((item) => mapAuditLogEntry(item as Record<string, unknown>)),
      total: body.total,
    }
  }
}

export const auditLogApi: AuditLogApi = useMockApi() ? new MockAuditLogApi() : new HttpAuditLogApi()

export const AUDIT_ACTION_OPTIONS = [
  { value: '', label: 'All actions' },
  { value: 'session_opened', label: 'Session opened' },
  { value: 'session_closed', label: 'Session closed' },
  { value: 'session_updated', label: 'Session updated' },
  { value: 'session_duplicated', label: 'Session duplicated' },
  { value: 'model_archived', label: 'Model archived' },
  { value: 'model_pinned', label: 'Model pinned' },
  { value: 'model_unpinned', label: 'Model unpinned' },
  { value: 'model_hidden', label: 'Model hidden' },
  { value: 'model_blacklisted', label: 'Model blacklisted' },
  { value: 'api_key_updated', label: 'API key updated' },
]
